import * as React from 'react'

import DefaultSortableRenderItem from '../generic/smartSortable/GenericSortableRenderItem'
import SmartSortableContainer from '../generic/smartSortable/SmartSortableContainer'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'

const ArrangeDevices = ({ devices, sortingChange, setVisibility, itemId }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [localDevices, setLocalDevices] = React.useState(devices)

  React.useEffect(() => {
    setLocalDevices(devices)
  }, [devices])

  const isMonitoringMode = devices?.length > 0 && devices[0].type !== undefined

  const getItemId = (item) => {
    if (itemId instanceof Function) return itemId(item)
    if (itemId) return item[itemId]
    return `${item.type}-${item.id}`
  }

  const ArrangeDeviceItem = ({ item }) => {
    if (isMonitoringMode) {
      const firstLabel = item.name
      const secondLabel = item.type === 'GROUP' ? (item.groupType?.code ?? 'Group') : (item.deviceType ?? '')
      return (
        <DefaultSortableRenderItem
          labels={[firstLabel, secondLabel]}
          itemId={getItemId(item)}
          icon={item.visible ? <Visibility /> : <VisibilityOff />}
          onIconClick={() => setVisibility(item.id)}
          variant={item.type === 'GROUP' ? 'group' : 'default'}
        />
      )
    }

    // Modo original
    return (
      <DefaultSortableRenderItem
        labels={[systemValueLabel(t_, 'DeviceTypeEnum', item.deviceType?.name ?? ''), item.name]}
        itemId={item.id}
        icon={item.visible ? <Visibility /> : <VisibilityOff />}
        onIconClick={(itemId) => setVisibility(itemId)}
      />
    )
  }

  const recalculateGlobalOrder = (items) => {
    let globalOrder = 1
    return items.map(item => {
      const updatedItem = { ...item, order: globalOrder++ }
      if (item.type === 'GROUP' && item.devices?.length > 0) {
        updatedItem.devices = item.devices.map(device => ({
          ...device,
          order: globalOrder++
        }))
      }
      return updatedItem
    })
  }

  const onGroupDevicesSortChanged = (groupId, newDevices) => {
    const updated = localDevices.map(item =>
      item.id === groupId ? { ...item, devices: newDevices } : item
    )
    const reordered = recalculateGlobalOrder(updated)
    setLocalDevices(reordered)
    sortingChange(reordered)
  }

  const onSortChanged = (items) => {
    if (sortingChange && typeof sortingChange === 'function') {
      if (isMonitoringMode) {
        const itemsWithChildren = items.map(item => {
          const original = localDevices.find(d => d.id === item.id && d.type === item.type)
          return { ...item, devices: original?.devices ?? [] }
        })
        const reordered = recalculateGlobalOrder(itemsWithChildren)
        setLocalDevices(reordered)
        sortingChange(reordered)
      } else {
        sortingChange(items)
      }
    }
  }

  const sortedDevices = isMonitoringMode
    ? [...localDevices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : localDevices

  return (
    <div style={{ width: '100%', margin: 'auto' }}>
      <SmartSortableContainer
        itemList={sortedDevices}
        itemId={getItemId}
        renderItem={ArrangeDeviceItem}
        onSortChanged={onSortChanged}
        renderChildren={isMonitoringMode
          ? (item) => {
              if (item.type !== 'GROUP' || !item.devices?.length) return null
              const sortedGroupDevices = [...item.devices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              return (
                <Box sx={{ ml: 4, pl: 2, borderLeft: `2px solid ${theme.palette.primary.main}` }}>
                  <SmartSortableContainer
                    itemList={sortedGroupDevices}
                    itemId={(device) => `DEVICE-${device.id}`}
                    renderItem={({ item: device }) => (
                      <DefaultSortableRenderItem
                        labels={[device.name, device.deviceType ?? '']}
                        itemId={`DEVICE-${device.id}`}
                        icon={device.visible ? <Visibility /> : <VisibilityOff />}
                        onIconClick={() => setVisibility(device.id)}
                      />
                    )}
                    onSortChanged={(newDevices) => onGroupDevicesSortChanged(item.id, newDevices)}
                  />
                </Box>
              )
            }
          : undefined}
      />
    </div>
  )
}

export default ArrangeDevices
